import DOMPurify from 'dompurify'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { MailMessage } from '@/features/preinvest/mail/types'
import { toast } from '@/lib/toast'
import { getContactMailMessage } from './mail'

/** Detaildialog voor één mailbericht in de contacttijdlijn (spec-48 WP4). */
export function MailDetailDialog({
  personId,
  messageId,
  open,
  onOpenChange,
}: {
  personId: string
  messageId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [message, setMessage] = useState<MailMessage | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!open || !messageId) {
        setMessage(null)
        return
      }
      setLoading(true)
      setMessage(null)
      try {
        const row = await getContactMailMessage(personId, messageId)
        if (!cancelled) setMessage(row)
      } catch (caught: unknown) {
        if (!cancelled) {
          toast.error(caught instanceof Error ? caught.message : 'Kon bericht niet laden.')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [open, personId, messageId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{message?.subject ?? 'Bericht'}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground">Laden…</p>
        ) : message?.body_html ? (
          <div
            className="vonk-prose text-sm leading-[1.55]"
            // Sanitized: external mailbox HTML never reaches the DOM raw.
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(message.body_html, {
                USE_PROFILES: { html: true },
                FORBID_TAGS: ['style'],
              }),
            }}
          />
        ) : (
          <p className="text-sm text-muted-foreground">Geen inhoud beschikbaar.</p>
        )}
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Sluiten
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
